import React, { useReducer, useCallback, useRef, useEffect, useState, useMemo } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogBody, DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Mail, Phone, FileText, Play, Square, RotateCcw, Download, CheckCircle2, XCircle, AlertTriangle, User, Send } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import MailMergeProgressList from './MailMergeProgressList';
import MailMergeResultsTable from './MailMergeResultsTable';
import { exportMailMergeToCSV, type MailMergeResult, type OutputType } from './mailmergeExport';
import { useCampaignTemplates, useSeedDefaultTemplates } from '@/hooks/useCampaignTemplates';
import MailMergeToCampaignDialog from '@/components/campaigns/MailMergeToCampaignDialog';
import { getSmartEmail } from '@/utils/smartEmailUtils';

interface MailMergeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leads: any[];
}

type Step = 'configure' | 'processing' | 'results';

interface State {
  step: Step;
  outputType: OutputType;
  outreachType: string;
  customPrompt: string;
  results: Map<string, MailMergeResult>;
  processedCount: number;
  isRunning: boolean;
}

type Action =
  | { type: 'SET_OUTPUT_TYPE'; payload: OutputType }
  | { type: 'SET_OUTREACH_TYPE'; payload: string }
  | { type: 'SET_CUSTOM_PROMPT'; payload: string }
  | { type: 'START_PROCESSING'; payload: Map<string, MailMergeResult> }
  | { type: 'UPDATE_RESULT'; payload: { id: string; result: Partial<MailMergeResult> } }
  | { type: 'INCREMENT_PROCESSED' }
  | { type: 'STOP' }
  | { type: 'SHOW_RESULTS' }
  | { type: 'RESET' }
  | { type: 'RETRY_FAILED'; payload: Map<string, MailMergeResult> };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_OUTPUT_TYPE':
      return { ...state, outputType: action.payload };
    case 'SET_OUTREACH_TYPE':
      return { ...state, outreachType: action.payload };
    case 'SET_CUSTOM_PROMPT':
      return { ...state, customPrompt: action.payload };
    case 'START_PROCESSING':
      return { ...state, step: 'processing', results: action.payload, processedCount: 0, isRunning: true };
    case 'UPDATE_RESULT': {
      const newResults = new Map(state.results);
      const existing = newResults.get(action.payload.id);
      if (existing) {
        newResults.set(action.payload.id, { ...existing, ...action.payload.result });
      }
      return { ...state, results: newResults };
    }
    case 'INCREMENT_PROCESSED':
      return { ...state, processedCount: state.processedCount + 1 };
    case 'STOP':
      return { ...state, isRunning: false };
    case 'SHOW_RESULTS':
      return { ...state, step: 'results', isRunning: false };
    case 'RESET':
      return initialState;
    case 'RETRY_FAILED': {
      return { ...state, step: 'processing', results: action.payload, processedCount: 0, isRunning: true };
    }
    default:
      return state;
  }
}

const initialState: State = {
  step: 'configure',
  outputType: 'both',
  outreachType: '',
  customPrompt: '',
  results: new Map(),
  processedCount: 0,
  isRunning: false,
};

interface TeamMember {
  id: string;
  first_name: string | null;
  last_name: string | null;
  job_title: string | null;
  work_email: string | null;
  work_phone: string | null;
  contact_number: string | null;
}

const MailMergeDialog: React.FC<MailMergeDialogProps> = ({ open, onOpenChange, leads }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { profile } = useAuth();
  const abortRef = useRef(false);
  const savedRunIdRef = useRef<string | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [selectedSenderId, setSelectedSenderId] = useState<string>(profile?.id || '');
  const [showCampaignDialog, setShowCampaignDialog] = useState(false);
  const [companyName, setCompanyName] = useState<string>('');
  
  const { data: campaignTemplates, isLoading: templatesLoading } = useCampaignTemplates(profile?.company_id ?? undefined);
  const seedMutation = useSeedDefaultTemplates();
  
  // Auto-seed default templates if company has none
  const [seedAttempted, setSeedAttempted] = useState(false);
  useEffect(() => {
    if (!open || !profile?.company_id || templatesLoading || seedAttempted) return;
    const mailMergeTemplates = campaignTemplates?.filter(t => t.category === 'mail-merge') || [];
    if (campaignTemplates && mailMergeTemplates.length === 0) {
      setSeedAttempted(true);
      seedMutation.mutate(profile.company_id);
    }
  }, [open, profile?.company_id, campaignTemplates, templatesLoading, seedAttempted]);
  
  // Default to first template when templates load
  useEffect(() => {
    if (campaignTemplates && campaignTemplates.length > 0 && !state.outreachType) {
      const firstActive = campaignTemplates.find(t => t.is_active);
      if (firstActive) {
        dispatch({ type: 'SET_OUTREACH_TYPE', payload: firstActive.id });
      }
    }
  }, [campaignTemplates, state.outreachType]);
  
  const selectedTemplate = useMemo(() => {
    if (!campaignTemplates || !state.outreachType) return null;
    return campaignTemplates.find(t => t.id === state.outreachType) || null;
  }, [state.outreachType, campaignTemplates]);
  useEffect(() => {
    if (!open || !profile?.company_id) return;
    // Fetch team members
    supabase
      .from('profiles')
      .select('id, first_name, last_name, job_title, work_email, work_phone, contact_number')
      .eq('company_id', profile.company_id)
      .then(({ data }) => {
        if (data) setTeamMembers(data);
      });
    // Fetch company name
    supabase
      .from('companies')
      .select('name')
      .eq('id', profile.company_id)
      .single()
      .then(({ data }) => {
        if (data) setCompanyName(data.name);
      });
    setSelectedSenderId(profile.id);
  }, [open, profile?.company_id, profile?.id]);
  // Smart email logic imported from shared utility

  const buildInitialResults = useCallback((leadsToProcess: any[], contactsMap: Map<string, any[]>): Map<string, MailMergeResult> => {
    const map = new Map<string, MailMergeResult>();
    leadsToProcess.forEach((lead) => {
      const smartResult = getSmartEmail(lead, contactsMap.get(lead.id));
      map.set(lead.id, {
        leadId: lead.id,
        leadName: smartResult?.contactName || lead.name || 'Unknown',
        leadEmail: smartResult?.email || '',
        leadPhone: lead.phone || '',
        leadAddress: lead.address || '',
        leadWebsite: lead.website || '',
        leadCategory: lead.category || '',
        leadStatus: lead.status || 'new',
        status: 'pending',
        contactName: smartResult?.contactName,
        contactPosition: smartResult?.contactPosition,
        emailSource: smartResult?.source,
      });
    });
    return map;
  }, []);

  const getSenderInfo = useCallback(() => {
    const sender = teamMembers.find(m => m.id === selectedSenderId) || 
      (profile ? { first_name: profile.first_name, last_name: profile.last_name, job_title: profile.job_title, work_email: profile.work_email, work_phone: profile.work_phone, contact_number: (profile as any)?.contact_number } : null);
    return {
      senderName: sender ? `${sender.first_name || ''} ${sender.last_name || ''}`.trim() : '',
      senderRole: sender?.job_title || 'Sales Representative',
      senderCompany: companyName || 'Our Company',
      senderEmail: (sender as any)?.work_email || profile?.work_email || '',
      senderPhone: (sender as any)?.work_phone || profile?.work_phone || '',
      senderContactNumber: (sender as any)?.contact_number || '',
    };
  }, [profile, teamMembers, selectedSenderId, companyName]);

  const fetchCompanyData = useCallback(async () => {
    if (!profile?.company_id) return null;
    const { data } = await supabase
      .from('companies')
      .select('name, description, web_search_data, industry, website, address, city, country, outreach_instructions')
      .eq('id', profile.company_id)
      .single();
    if (!data) return null;
    return {
      name: data.name,
      summary: data.description,
      address: (data as any).address,
      city: (data as any).city,
      country: (data as any).country,
      outreachInstructions: (data as any).outreach_instructions || null,
      ...(data.web_search_data && typeof data.web_search_data === 'object' ? data.web_search_data as Record<string, unknown> : {}),
    };
  }, [profile?.company_id]);

  const createRunUpfront = useCallback(async (leadIds: string[], resultsMap: Map<string, MailMergeResult>) => {
    if (!profile?.company_id) return;
    const outreachLabel = selectedTemplate?.name || 'Mail Merge';
    const name = `Mail Merge - ${outreachLabel} - ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    const resultsArray = Array.from(resultsMap.values());

    const payload = {
      company_id: profile.company_id,
      created_by: profile.id,
      name,
      outreach_type: state.outreachType,
      output_type: state.outputType,
      custom_prompt: state.customPrompt || null,
      status: 'in_progress',
      total_count: resultsArray.length,
      success_count: 0,
      failed_count: 0,
      results: resultsArray,
      lead_ids: leadIds,
      sender_id: selectedSenderId || null,
      template_id: state.outreachType || null,
      template_mode: (selectedTemplate as any)?.template_mode || 'guide',
    };

    const { data, error } = await supabase
      .from('mailmerge_runs' as any)
      .insert(payload as any)
      .select('id')
      .single();
    if (error) {
      toast.error('Failed to save mail merge run');
    } else if (data) {
      savedRunIdRef.current = (data as any).id;
    }
  }, [profile, state.outreachType, state.outputType, state.customPrompt, selectedSenderId, selectedTemplate]);

  const updateRunProgress = useCallback(async (resultsMap: Map<string, MailMergeResult>, isFinal: boolean, wasAborted: boolean) => {
    if (!savedRunIdRef.current) return;
    const resultsArray = Array.from(resultsMap.values());
    const sc = resultsArray.filter(r => r.status === 'success').length;
    const fc = resultsArray.filter(r => r.status === 'failed').length;
    const pending = resultsArray.filter(r => r.status === 'pending' || r.status === 'generating').length;
    const runStatus = isFinal
      ? (wasAborted ? 'partial' : (fc === 0 ? 'completed' : 'partial'))
      : (pending > 0 ? 'in_progress' : (fc === 0 ? 'completed' : 'partial'));

    const { error } = await supabase
      .from('mailmerge_runs' as any)
      .update({
        results: resultsArray,
        success_count: sc,
        failed_count: fc,
        status: runStatus,
        updated_at: new Date().toISOString(),
      } as any)
      .eq('id', savedRunIdRef.current);
    if (error) toast.error('Failed to update mail merge run');
  }, []);

  const processLeads = useCallback(async (leadsToProcess: any[], resultsMap: Map<string, MailMergeResult>) => {
    abortRef.current = false;
    const senderInfo = getSenderInfo();
    const companyData = await fetchCompanyData();

    const leadIds = leadsToProcess.map(l => l.id);
    const batchSize = 10;
    let offset = 0;

    // Mark all leads as generating
    for (const lead of leadsToProcess) {
      dispatch({ type: 'UPDATE_RESULT', payload: { id: lead.id, result: { status: 'generating' } } });
    }

    while (offset < leadIds.length && !abortRef.current) {
      try {
        const { data, error } = await supabase.functions.invoke('generate-mailmerge-batch', {
          body: {
            lead_ids: leadIds,
            outreach_type: selectedTemplate?.name || 'custom',
            custom_prompt: state.customPrompt,
            sender_info: senderInfo,
            company_data: companyData,
            output_type: state.outputType,
            ...(selectedTemplate ? {
              template_content: {
                subject: selectedTemplate.subject_template,
                body: selectedTemplate.body_template,
                name: selectedTemplate.name,
              },
              template_mode: (selectedTemplate as any).template_mode || 'guide',
            } : {}),
            offset,
            batch_size: batchSize,
          },
        });

        if (error) throw new Error(error.message || 'Edge function error');
        if (data?.error) throw new Error(data.error);

        // Process batch results
        const batchResults = data?.results || [];
        for (const r of batchResults) {
          if (r.success) {
            const updatedResult: Partial<MailMergeResult> = {
              status: 'success',
              subject: r.email?.subject,
              emailBody: r.email?.body,
              callScript: r.callScript,
              ...(r.recipientEmail ? { leadEmail: r.recipientEmail } : {}),
              ...(r.recipientContactName ? { contactName: r.recipientContactName } : {}),
            };
            resultsMap.set(r.leadId, { ...resultsMap.get(r.leadId)!, ...updatedResult });
            dispatch({ type: 'UPDATE_RESULT', payload: { id: r.leadId, result: updatedResult } });
          } else {
            const updatedResult: Partial<MailMergeResult> = { status: 'failed', error: r.error || 'Unknown error' };
            resultsMap.set(r.leadId, { ...resultsMap.get(r.leadId)!, ...updatedResult });
            dispatch({ type: 'UPDATE_RESULT', payload: { id: r.leadId, result: updatedResult } });
          }
          dispatch({ type: 'INCREMENT_PROCESSED' });
        }

        // Incremental save after each batch
        await updateRunProgress(resultsMap, false, false);

        if (data?.done) break;
        offset = data?.nextOffset ?? (offset + batchSize);
      } catch (err: any) {
        // Mark remaining leads in this batch as failed
        const batchLeadIds = leadIds.slice(offset, offset + batchSize);
        for (const id of batchLeadIds) {
          const existing = resultsMap.get(id);
          if (existing && existing.status === 'generating') {
            const updatedResult: Partial<MailMergeResult> = { status: 'failed', error: err.message || 'Batch failed' };
            resultsMap.set(id, { ...existing, ...updatedResult });
            dispatch({ type: 'UPDATE_RESULT', payload: { id, result: updatedResult } });
            dispatch({ type: 'INCREMENT_PROCESSED' });
          }
        }
        // Save progress even on error
        await updateRunProgress(resultsMap, false, false);
        offset += batchSize;
      }
    }

    dispatch({ type: 'SHOW_RESULTS' });
    await updateRunProgress(resultsMap, true, abortRef.current);
  }, [state.outreachType, state.customPrompt, state.outputType, getSenderInfo, fetchCompanyData, updateRunProgress, selectedTemplate]);

  const handleGenerate = useCallback(async () => {
    // Batch-fetch key contacts for all selected leads
    const { data: allContacts } = await supabase
      .from('lead_key_contacts')
      .select('lead_id, name, email, is_primary, position, status')
      .in('lead_id', leads.map(l => l.id))
      .not('email', 'is', null);

    const contactsMap = new Map<string, any[]>();
    (allContacts || []).forEach((c: any) => {
      const list = contactsMap.get(c.lead_id) || [];
      list.push(c);
      contactsMap.set(c.lead_id, list);
    });

    const leadIds = leads.map(l => l.id);
    const resultsMap = buildInitialResults(leads, contactsMap);
    
    // Create the run immediately so it persists even if processing fails
    await createRunUpfront(leadIds, resultsMap);
    
    dispatch({ type: 'START_PROCESSING', payload: resultsMap });
    processLeads(leads, resultsMap);
  }, [leads, buildInitialResults, processLeads, createRunUpfront]);

  const handleStop = useCallback(() => {
    abortRef.current = true;
    dispatch({ type: 'STOP' });
  }, []);

  const handleRetryFailed = useCallback(() => {
    const failedLeads = leads.filter((l) => {
      const r = state.results.get(l.id);
      return r?.status === 'failed';
    });
    if (failedLeads.length === 0) return;

    const newResults = new Map(state.results);
    failedLeads.forEach((l) => {
      const existing = newResults.get(l.id);
      if (existing) {
        newResults.set(l.id, { ...existing, status: 'pending', error: undefined });
      }
    });

    dispatch({ type: 'RETRY_FAILED', payload: newResults });
    processLeads(failedLeads, newResults);
  }, [leads, state.results, processLeads]);

  const handleExport = useCallback(() => {
    exportMailMergeToCSV(state.results, state.outreachType, state.outputType);
  }, [state.results, state.outreachType, state.outputType]);

  const handleClose = useCallback((val: boolean) => {
    if (!val) {
      abortRef.current = true;
      savedRunIdRef.current = null;
      dispatch({ type: 'RESET' });
    }
    onOpenChange(val);
  }, [onOpenChange]);

  const successCount = Array.from(state.results.values()).filter(r => r.status === 'success').length;
  const failedCount = Array.from(state.results.values()).filter(r => r.status === 'failed').length;
  const totalCount = state.results.size;
  const progress = totalCount > 0 ? (state.processedCount / totalCount) * 100 : 0;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Mail Merge
          </DialogTitle>
          <DialogDescription>
            {state.step === 'configure' && `Generate personalized content for ${leads.length} selected lead${leads.length === 1 ? '' : 's'}`}
            {state.step === 'processing' && `Processing ${state.processedCount} of ${totalCount} leads…`}
            {state.step === 'results' && `Generation complete`}
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          {/* STEP 1: Configure */}
          {state.step === 'configure' && (
            <div className="space-y-5 px-[5px]">
              <div>
                <label className="text-sm font-medium mb-2 block">Output Type</label>
                <ToggleGroup
                  type="single"
                  value={state.outputType}
                  onValueChange={(val) => val && dispatch({ type: 'SET_OUTPUT_TYPE', payload: val as OutputType })}
                  className="justify-start"
                >
                  <ToggleGroupItem value="email" className="gap-1.5">
                    <Mail className="h-4 w-4" /> Email Only
                  </ToggleGroupItem>
                  <ToggleGroupItem value="call" className="gap-1.5">
                    <Phone className="h-4 w-4" /> Call Script Only
                  </ToggleGroupItem>
                  <ToggleGroupItem value="both" className="gap-1.5">
                    <FileText className="h-4 w-4" /> Both
                  </ToggleGroupItem>
                </ToggleGroup>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Outreach Type</label>
                <Select
                  value={state.outreachType}
                  onValueChange={(val) => dispatch({ type: 'SET_OUTREACH_TYPE', payload: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {campaignTemplates && campaignTemplates.filter(t => t.is_active).map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name}
                      </SelectItem>
                    ))}
                    {(!campaignTemplates || campaignTemplates.filter(t => t.is_active).length === 0) && (
                      <SelectItem value="__loading" disabled>Loading templates…</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {teamMembers.length > 1 && (
                <div>
                  <label className="text-sm font-medium mb-2 block flex items-center gap-1.5">
                    <User className="h-3.5 w-3.5" /> Send As
                  </label>
                  <Select value={selectedSenderId} onValueChange={setSelectedSenderId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select sender" />
                    </SelectTrigger>
                    <SelectContent>
                      {teamMembers.map((m) => (
                        <SelectItem key={m.id} value={m.id}>
                          {`${m.first_name || ''} ${m.last_name || ''}`.trim() || 'Unnamed'}
                          {m.job_title ? ` — ${m.job_title}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <label className="text-sm font-medium mb-2 block">Additional Context (optional)</label>
                <Textarea
                  value={state.customPrompt}
                  onChange={(e) => dispatch({ type: 'SET_CUSTOM_PROMPT', payload: e.target.value })}
                  placeholder="Add any extra context for the AI, e.g., specific products to mention, tone preferences, or key selling points…"
                  className="min-h-[100px]"
                />
              </div>
            </div>
          )}

          {/* STEP 2: Processing */}
          {state.step === 'processing' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium">{state.processedCount} / {totalCount} leads</span>
                  <span className="text-muted-foreground">{Math.round(progress)}%</span>
                </div>
                <Progress value={progress} className="h-2" />
              </div>
              <MailMergeProgressList results={state.results} />
            </div>
          )}

          {/* STEP 3: Results */}
          {state.step === 'results' && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/40 border border-border/60">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <div className="text-lg font-bold">{totalCount}</div>
                    <div className="text-xs text-muted-foreground">Total</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 p-3 rounded-lg bg-green-500/5 border border-green-500/20">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  <div>
                    <div className="text-lg font-bold text-green-600">{successCount}</div>
                    <div className="text-xs text-muted-foreground">Success</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/5 border border-destructive/20">
                  <XCircle className="h-4 w-4 text-destructive" />
                  <div>
                    <div className="text-lg font-bold text-destructive">{failedCount}</div>
                    <div className="text-xs text-muted-foreground">Failed</div>
                  </div>
                </div>
              </div>
              <MailMergeResultsTable results={state.results} outputType={state.outputType} />
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          {state.step === 'configure' && (
            <Button onClick={handleGenerate} className="gap-1.5">
              <Play className="h-4 w-4" /> Generate
            </Button>
          )}
          {state.step === 'processing' && (
            <Button variant="destructive" onClick={handleStop} disabled={!state.isRunning} className="gap-1.5">
              <Square className="h-4 w-4" /> Stop
            </Button>
          )}
          {state.step === 'results' && (
            <div className="flex gap-2 flex-wrap">
              {failedCount > 0 && (
                <Button variant="outline" onClick={handleRetryFailed} className="gap-1.5">
                  <RotateCcw className="h-4 w-4" /> Retry Failed ({failedCount})
                </Button>
              )}
              <Button onClick={handleExport} className="gap-1.5">
                <Download className="h-4 w-4" /> Export CSV
              </Button>
              {(state.outputType === 'email' || state.outputType === 'both') && successCount > 0 && (
                <Button onClick={() => setShowCampaignDialog(true)} className="gap-1.5">
                  <Send className="h-4 w-4" /> Send as Campaign
                </Button>
              )}
            </div>
          )}
        </DialogFooter>
      </DialogContent>

      {showCampaignDialog && savedRunIdRef.current && (
        <MailMergeToCampaignDialog
          open={showCampaignDialog}
          onOpenChange={setShowCampaignDialog}
          run={{
            id: savedRunIdRef.current,
            name: `Mail Merge – ${state.outreachType}`,
            outreach_type: state.outreachType,
            results: Array.from(state.results.values()),
            success_count: successCount,
            failed_count: failedCount,
            total_count: totalCount,
          }}
        />
      )}
    </Dialog>
  );
};

export default MailMergeDialog;
